import { useEffect, useState } from "react";
import { api, type Search } from "../api";

const empty: Search = { name: "", role: "", keywords: [], skills: [], location: "remote", seniority: [], min_score: 60, max_days_old: 1 };

export default function Searches() {
  const [searches, setSearches] = useState<Search[]>([]);
  const [draft, setDraft] = useState<Search | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setError("");
    try {
      setSearches(await api.searches());
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function save() {
    if (!draft || !draft.name || !draft.role) return;
    try {
      await api.saveSearch(draft);
      setDraft(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function remove(name: string) {
    try {
      await api.deleteSearch(name);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div>
      <h2>Job searches</h2>
      <p className="muted">
        The agent runs these every day against every enabled source. <code>min_score</code> gates
        which jobs move into the pipeline.
      </p>
      {error && <p className="err">{error}</p>}
      <div className="card">
        <table>
          <thead>
            <tr><th>Name</th><th>Role</th><th>Keywords</th><th>Skills</th><th>Location</th><th>Min score</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {searches.map((s) => (
              <tr key={s.name}>
                <td><b>{s.name}</b></td>
                <td>{s.role}</td>
                <td>{s.keywords.map((k) => <span key={k} className="tag">{k}</span>)}</td>
                <td>{s.skills.map((k) => <span key={k} className="tag">{k}</span>)}</td>
                <td className="muted">{s.location || "any"}</td>
                <td>{s.min_score}</td>
                <td>
                  <div className="row-actions">
                    <button onClick={() => setDraft({ ...s })}>Edit</button>
                    <button className="danger" onClick={() => remove(s.name)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="primary" style={{ marginTop: 14 }} onClick={() => setDraft({ ...empty })}>
          New search
        </button>
      </div>

      {draft && (
        <div className="card">
          <h3>{draft.name ? `Edit: ${draft.name}` : "New search"}</h3>
          <div className="form-row">
            <div>
              <label>Name</label>
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label>Role title</label>
              <input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Keywords (comma separated)</label>
              <input value={draft.keywords.join(", ")} onChange={(e) => setDraft({ ...draft, keywords: split(e.target.value) })} />
            </div>
            <div>
              <label>Skills (comma separated)</label>
              <input value={draft.skills.join(", ")} onChange={(e) => setDraft({ ...draft, skills: split(e.target.value) })} />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Location (e.g. remote)</label>
              <input value={draft.location ?? ""} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
            </div>
            <div>
              <label>Seniority (comma separated)</label>
              <input value={draft.seniority.join(", ")} onChange={(e) => setDraft({ ...draft, seniority: split(e.target.value) })} />
            </div>
          </div>
          <div className="form-row">
            <div>
              <label>Min score (0-100)</label>
              <input type="number" value={draft.min_score} onChange={(e) => setDraft({ ...draft, min_score: Number(e.target.value) })} />
            </div>
            <div>
              <label>Max days old</label>
              <input type="number" value={draft.max_days_old} onChange={(e) => setDraft({ ...draft, max_days_old: Number(e.target.value) })} />
            </div>
          </div>
          <div className="form-actions">
            <button className="primary" onClick={save}>Save</button>
            <button onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  function split(v: string): string[] {
    return v.split(",").map((s) => s.trim()).filter(Boolean);
  }
}